import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  RefreshCw, ChevronDown, ChevronUp, Mail, Clock, Calendar as CalendarIcon, ExternalLink, Copy, Check, Archive, AlertTriangle
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EmailComposer } from "./EmailComposer";
import { ContractBuilder } from "./ContractBuilder";
import { ItineraryEditor } from "./ItineraryEditor";
import { InlineEmailPanel } from "./InlineEmailPanel";
import { InquiryNotesPanel } from "./InquiryNotesPanel";
import { VendorContactsPanel } from "./VendorContactsPanel";
import { DocumentsPanel } from "./DocumentsPanel";
import { InlinePdfViewer } from "./InlinePdfViewer";
import { InquiryDetailTabs } from "./InquiryDetailTabs";
import { RsvpStatusBadge } from "./RsvpStatusBadge";
import { useToast } from "@/hooks/use-toast";
import { format, isValid, parse, addDays, setHours, setMinutes } from "date-fns";
import { toZonedTime, fromZonedTime, format as formatTz } from "date-fns-tz";
import { logActivity } from "@/lib/activityLogger";
import { cn } from "@/lib/utils";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Time slots for admin scheduling (business hours)
const ADMIN_TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
];

const formatTimeSlot = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

interface InquirySubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  venue_address: string;
  event_date: string;
  event_start_time: string;
  event_end_time: string;
  load_in_time: string | null;
  notes: string | null;
  created_at: string;
  user_id: string | null;
  event_dates_json: any;
  session_id: string | null;
  total_cost: number | null;
  total_savings: number | null;
  packages_json: any;
  cultural_analysis_json: any;
  songs_json: any;
  occasion: string | null;
  vibes: string[] | null;
  user_timezone: string | null;
  status: string;
  scheduled_call_at: string | null;
  scheduled_call_notes: string | null;
  vendor_contacts_json: any;
  calendar_rsvp_status: string | null;
  calendar_rsvp_at: string | null;
  has_incident?: boolean;
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30', rowColor: 'bg-yellow-950/30 border-l-4 border-l-yellow-500' },
  { value: 'call_scheduled', label: 'Call Scheduled', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30', rowColor: 'bg-purple-950/30 border-l-4 border-l-purple-500' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30', rowColor: 'bg-blue-950/30 border-l-4 border-l-blue-500' },
  { value: 'contracted', label: 'Contracted', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30', rowColor: 'bg-orange-950/30 border-l-4 border-l-orange-500' },
  { value: 'signed', label: 'Signed', color: 'bg-green-500/20 text-green-300 border-green-500/30', rowColor: 'bg-green-950/30 border-l-4 border-l-green-500' },
  { value: 'invoiced', label: 'Invoiced', color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30', rowColor: 'bg-cyan-950/30 border-l-4 border-l-cyan-500' },
  { value: 'paid', label: 'Paid', color: 'bg-teal-500/20 text-teal-300 border-teal-500/30', rowColor: 'bg-teal-950/30 border-l-4 border-l-teal-500' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', rowColor: 'bg-emerald-950/30 border-l-4 border-l-emerald-500' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-500/20 text-red-300 border-red-500/30', rowColor: 'bg-red-950/30 border-l-4 border-l-red-500' },
];

const getRowColorByStatus = (status: string) => {
  const statusOption = STATUS_OPTIONS.find(s => s.value === status);
  return statusOption?.rowColor || '';
};

export const InquiriesTable = () => {
  const [inquiries, setInquiries] = useState<InquirySubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<InquirySubmission | null>(null);
  const [callDate, setCallDate] = useState<Date | undefined>(undefined);
  const [callTime, setCallTime] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [contractBuilderOpen, setContractBuilderOpen] = useState(false);
  const [itineraryEditorOpen, setItineraryEditorOpen] = useState(false);
  const [editingItinerary, setEditingItinerary] = useState<any | null>(null);
  const [contractPdf, setContractPdf] = useState<{ filename: string; content: string } | null>(null);
  const [inquiryContracts, setInquiryContracts] = useState<Record<string, any[]>>({});
  const [inquiryAssignments, setInquiryAssignments] = useState<Record<string, any[]>>({});
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isCheckingExpired, setIsCheckingExpired] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');
  const [previewContract, setPreviewContract] = useState<{ 
    html?: string; 
    pdfUrl?: string; 
    imageUrl?: string; 
    audioUrl?: string;
    videoUrl?: string;
    textContent?: string;
    officeUrl?: string;
    contractNumber: string;
    fileType?: string;
  } | null>(null);
  const [editingContract, setEditingContract] = useState<any | null>(null);
  
  const { toast } = useToast();

  // Fetch functions
  const fetchContractsForInquiry = async (inquiryId: string) => {
    const { data, error } = await supabase
      .from("inquiry_contracts")
      .select("*")
      .eq("inquiry_id", inquiryId)
      .order("created_at", { ascending: false });
    if (!error && data) {
      setInquiryContracts(prev => ({ ...prev, [inquiryId]: data }));
    }
  };

  const fetchAssignmentsForInquiry = async (inquiryId: string) => {
    const { data, error } = await supabase
      .from("musician_assignments")
      .select("*, musician:musicians(id, name, region, phone)")
      .eq("inquiry_id", inquiryId);
    if (!error && data) {
      setInquiryAssignments(prev => ({ ...prev, [inquiryId]: data }));
    }
  };

  const fetchInquiries = async () => {
    setIsLoading(true);
    
    // Fetch inquiries and unread counts in parallel
    const [inquiriesRes, unreadRes] = await Promise.all([
      supabase
        .from("contact_submissions")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("inquiry_inbound_emails")
        .select("inquiry_id")
        .is("read_at", null)
    ]);

    if (inquiriesRes.error) {
      toast({ title: "Error", description: "Failed to load inquiries", variant: "destructive" });
    } else {
      // Build unread counts map
      const counts: Record<string, number> = {};
      (unreadRes.data || []).forEach(row => {
        counts[row.inquiry_id] = (counts[row.inquiry_id] || 0) + 1;
      });
      setUnreadCounts(counts);
      
      // Sort: inquiries with unread messages first, then by created_at
      const sortedInquiries = ((inquiriesRes.data as InquirySubmission[]) || []).sort((a, b) => {
        const aUnread = counts[a.id] || 0;
        const bUnread = counts[b.id] || 0;
        
        // Unread first
        if (aUnread > 0 && bUnread === 0) return -1;
        if (bUnread > 0 && aUnread === 0) return 1;
        
        // Then by created_at (most recent first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      setInquiries(sortedInquiries);
    }
    setIsLoading(false);
  };

  useEffect(() => { fetchInquiries(); }, []);

  // Realtime subscription for inbound emails to detect new messages
  useEffect(() => {
    const channel = supabase
      .channel('inbound-emails-global')
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "inquiry_inbound_emails",
        },
        (payload) => {
          console.log('New inbound email detected:', payload);
          // Update unread count for this inquiry
          const inquiryId = payload.new.inquiry_id;
          setUnreadCounts(prev => ({
            ...prev,
            [inquiryId]: (prev[inquiryId] || 0) + 1
          }));
          // Re-sort inquiries to bring this one to top
          setInquiries(prev => {
            const updated = [...prev];
            return updated.sort((a, b) => {
              const newCounts = { ...unreadCounts, [inquiryId]: (unreadCounts[inquiryId] || 0) + 1 };
              const aUnread = newCounts[a.id] || 0;
              const bUnread = newCounts[b.id] || 0;
              if (aUnread > 0 && bUnread === 0) return -1;
              if (bUnread > 0 && aUnread === 0) return 1;
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            });
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [unreadCounts]);

  // Realtime subscription for inquiry_contracts to show new documents immediately
  useEffect(() => {
    if (!expandedRow) return;
    
    const channel = supabase
      .channel(`inquiry-contracts-${expandedRow}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "inquiry_contracts",
          filter: `inquiry_id=eq.${expandedRow}`,
        },
        () => {
          console.log('Document change detected for inquiry:', expandedRow);
          fetchContractsForInquiry(expandedRow);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [expandedRow]);

  const toggleExpand = (id: string) => {
    const newExpandedId = expandedRow === id ? null : id;
    setExpandedRow(newExpandedId);
    if (newExpandedId) {
      if (!inquiryContracts[newExpandedId]) {
        fetchContractsForInquiry(newExpandedId);
      }
      if (!inquiryAssignments[newExpandedId]) {
        fetchAssignmentsForInquiry(newExpandedId);
      }
      // Clear unread count for this inquiry when expanded (emails will be marked read in InlineEmailPanel)
      if (unreadCounts[newExpandedId] > 0) {
        setUnreadCounts(prev => ({ ...prev, [newExpandedId]: 0 }));
      }
    }
  };

  // Utility functions
  const formatTime = (time: string) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? "PM" : "AM"}`;
  };

  const parseFlexibleDate = (value: unknown): Date | null => {
    if (typeof value !== "string") return null;
    const raw = value.trim();
    if (!raw) return null;
    const candidates = [
      parse(raw, "M/d/yyyy", new Date()),
      parse(raw, "MM/dd/yyyy", new Date()),
      parse(raw, "yyyy-MM-dd", new Date()),
      new Date(raw),
    ];
    return candidates.find((d) => isValid(d)) ?? null;
  };

  const safeFormatEventDate = (value: unknown, fmt: string) => {
    const parsed = parseFlexibleDate(value);
    if (!parsed) return typeof value === "string" ? value : "";
    return format(parsed, fmt);
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return "$0";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const handleStatusChange = async (inquiryId: string, newStatus: string, oldStatus?: string) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ status: newStatus })
      .eq("id", inquiryId);
    if (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    } else {
      setInquiries(prev => prev.map(inq => inq.id === inquiryId ? { ...inq, status: newStatus } : inq));
      const statusLabel = STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus;
      logActivity({ inquiryId, action: 'status_changed', description: `Status changed to ${statusLabel}`, metadata: { oldStatus, newStatus } });
    }
  };

  // Schedule call functions
  const openScheduleDialog = (inquiry: InquirySubmission) => {
    setSelectedInquiry(inquiry);
    if (inquiry.scheduled_call_at) {
      const clientTz = inquiry.user_timezone || 'America/Chicago';
      const zonedDate = toZonedTime(new Date(inquiry.scheduled_call_at), clientTz);
      setCallDate(zonedDate);
      setCallTime(formatTz(zonedDate, "HH:mm"));
    } else {
      setCallDate(addDays(new Date(), 1)); // Default to tomorrow
      setCallTime("10:00");
    }
    setCallNotes(inquiry.scheduled_call_notes || "");
    setCalendarOpen(false);
    setScheduleDialogOpen(true);
  };

  const handleScheduleCall = async () => {
    if (!selectedInquiry || !callDate || !callTime) return;
    const [hours, minutes] = callTime.split(':').map(Number);
    
    // Build the date/time in the CLIENT's timezone
    // Admin selects a time like "9:00 AM" which should be 9 AM in client's timezone
    let scheduledDateTime = new Date(callDate);
    scheduledDateTime = setHours(scheduledDateTime, hours);
    scheduledDateTime = setMinutes(scheduledDateTime, minutes);
    scheduledDateTime.setSeconds(0);
    scheduledDateTime.setMilliseconds(0);
    
    // Convert from client's timezone to UTC using fromZonedTime
    const clientTimezone = selectedInquiry.user_timezone || 'America/Chicago';
    const utcDateTime = fromZonedTime(scheduledDateTime, clientTimezone);
    
    // Check if this is a reschedule - refresh inquiry state to get latest data
    // This prevents stale state issues after cancellation
    const { data: freshInquiry } = await supabase
      .from("contact_submissions")
      .select("scheduled_call_at")
      .eq("id", selectedInquiry.id)
      .single();
    
    const isReschedule = !!(freshInquiry?.scheduled_call_at);
    const previousCallDate = freshInquiry?.scheduled_call_at 
      ? new Date(freshInquiry.scheduled_call_at) 
      : null;
    
    try {
      const { error } = await supabase
        .from("contact_submissions")
        .update({ 
          scheduled_call_at: utcDateTime.toISOString(), 
          scheduled_call_notes: callNotes,
          status: 'call_scheduled'
        })
        .eq("id", selectedInquiry.id);
      
      if (error) throw error;

      // Send calendar invite to client
      try {
        // Parse event dates for multi-event support
        let eventDates = undefined;
        if (selectedInquiry.event_dates_json && Array.isArray(selectedInquiry.event_dates_json)) {
          eventDates = selectedInquiry.event_dates_json.map((evt: any) => ({
            date: evt.date,
            startTime: evt.startTime,
            endTime: evt.endTime,
            venue: evt.venue || selectedInquiry.venue_address,
            occasion: evt.occasion || selectedInquiry.occasion,
            vibes: evt.vibes || selectedInquiry.vibes,
          }));
        }

        await supabase.functions.invoke("send-calendar-invite", {
          body: {
            recipientEmail: selectedInquiry.email,
            recipientName: selectedInquiry.name,
            scheduledAt: utcDateTime.toISOString(),
            timezone: clientTimezone,
            notes: callNotes || undefined,
            venueAddress: selectedInquiry.venue_address,
            eventOccasion: selectedInquiry.occasion,
            isReschedule,
            previousCallDate: previousCallDate?.toISOString(),
            inquiryId: selectedInquiry.id,
            eventDates,
            eventDate: selectedInquiry.event_date,
            eventStartTime: selectedInquiry.event_start_time,
            eventEndTime: selectedInquiry.event_end_time,
            vibes: selectedInquiry.vibes,
            phone: selectedInquiry.phone,
          },
        });
        const toastTitle = isReschedule ? "Call Rescheduled! 📅" : "Call Scheduled! 📅";
        toast({ title: toastTitle, description: `Calendar invite sent to ${selectedInquiry.email}` });
      } catch (calendarError) {
        console.warn("Failed to send calendar invite:", calendarError);
        toast({ title: "Call Scheduled", description: `Call scheduled for ${format(scheduledDateTime, "PPpp")} (calendar invite failed)` });
      }

      const actionType = isReschedule ? 'call_rescheduled' : 'call_scheduled';
      logActivity({ inquiryId: selectedInquiry.id, action: actionType, description: `Call ${isReschedule ? 'rescheduled' : 'scheduled'} for ${format(scheduledDateTime, "PPpp")}`, metadata: { scheduledAt: utcDateTime.toISOString(), notes: callNotes, previousCallDate: previousCallDate?.toISOString() } });
      setScheduleDialogOpen(false);
      fetchInquiries();
    } catch (err: any) {
      toast({ title: "Error", description: "Failed to schedule call", variant: "destructive" });
    }
  };

  const cancelScheduledCall = async (inquiryId: string) => {
    // Find the inquiry to get details for cancellation email
    const inquiry = inquiries.find(i => i.id === inquiryId);
    
    if (inquiry?.scheduled_call_at) {
      // Send cancellation notification to client
      try {
        await supabase.functions.invoke('send-calendar-invite', {
          body: {
            mode: 'cancel',
            recipientEmail: inquiry.email,
            recipientName: inquiry.name,
            scheduledAt: inquiry.scheduled_call_at,
            timezone: inquiry.user_timezone || 'America/Chicago',
            inquiryId: inquiry.id,
            phone: inquiry.phone,
          },
        });
        console.log('✅ Cancellation email sent to client');
      } catch (calendarError) {
        console.warn("⚠️ Failed to send cancellation email:", calendarError);
      }
    }
    
    const { error } = await supabase
      .from("contact_submissions")
      .update({ scheduled_call_at: null, scheduled_call_notes: null, status: 'pending' })
      .eq("id", inquiryId);
    if (!error) {
      toast({ title: "Call Cancelled", description: "Client notified and status reverted to pending" });
      logActivity({ inquiryId, action: 'call_cancelled', description: 'Scheduled call cancelled - client notified and status reverted to pending', metadata: {} });
      fetchInquiries();
    }
  };

  // Contract handlers
  const downloadContract = async (storagePath: string, contractNumber: string) => {
    try {
      const { data, error } = await supabase.storage.from('contracts').download(storagePath);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contract-${contractNumber}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({ title: "Download failed", description: error.message, variant: "destructive" });
    }
  };

  const previewContractFile = async (storagePath: string, contractNumber: string, inquiryId?: string) => {
    try {
      console.log('📄 Preview requested:', { storagePath, contractNumber, inquiryId });
      const lowerPath = storagePath.toLowerCase();
      const extension = lowerPath.split('.').pop() || '';
      console.log('🔍 File extension detected:', extension);
      
      // Determine file type
      const isPdf = extension === 'pdf';
      const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(extension);
      const isAudio = ['mp3', 'wav', 'm4a', 'ogg', 'aac', 'flac'].includes(extension);
      const isVideo = ['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(extension);
      const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension);
      const isText = ['txt', 'md', 'json', 'csv', 'xml', 'log', 'yaml', 'yml'].includes(extension);
      const isHtml = ['html', 'htm'].includes(extension);
      console.log('📋 File type flags:', { isPdf, isImage, isAudio, isVideo, isOffice, isText, isHtml });
      
      // Get signed URL for most file types
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('contracts')
        .createSignedUrl(storagePath, 600); // 10 minute expiry
      
      if (signedUrlError) throw signedUrlError;
      const signedUrl = signedUrlData.signedUrl;
      console.log('✅ Signed URL created:', signedUrl.substring(0, 80) + '...');
      
      if (isImage) {
        console.log('🖼️ Setting image preview');
        setPreviewContract({ imageUrl: signedUrl, contractNumber, fileType: extension });
      } else if (isPdf) {
        console.log('📄 Fetching PDF for blob preview...');
        // Fetch PDF and create blob URL to bypass iframe restrictions
        const response = await fetch(signedUrl);
        if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        console.log('📄 Setting PDF preview with blob URL');
        setPreviewContract({ pdfUrl: blobUrl, contractNumber, fileType: 'pdf' });
      } else if (isAudio) {
        console.log('🎵 Setting audio preview');
        setPreviewContract({ audioUrl: signedUrl, contractNumber, fileType: extension });
      } else if (isVideo) {
        console.log('🎬 Setting video preview');
        setPreviewContract({ videoUrl: signedUrl, contractNumber, fileType: extension });
      } else if (isOffice) {
        // Use Microsoft Office Online viewer for Word/Excel/PowerPoint
        const encodedUrl = encodeURIComponent(signedUrl);
        const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
        console.log('📊 Setting Office preview');
        setPreviewContract({ officeUrl: officeViewerUrl, contractNumber, fileType: extension });
      } else if (isText) {
        // Download and display as text
        const { data, error } = await supabase.storage.from('contracts').download(storagePath);
        if (error) throw error;
        const textContent = await data.text();
        console.log('📝 Setting text preview');
        setPreviewContract({ textContent, contractNumber, fileType: extension });
      } else if (isHtml) {
        // Download and display HTML inline
        const { data, error } = await supabase.storage.from('contracts').download(storagePath);
        if (error) throw error;
        const htmlContent = await data.text();
        console.log('🌐 Setting HTML preview');
        setPreviewContract({ html: htmlContent, contractNumber, fileType: 'html' });
      } else {
        // Unknown file type - try to display as text or show download link
        console.log('❌ Unknown file type:', extension);
        toast({ 
          title: "Preview unavailable", 
          description: `Cannot preview .${extension} files. Try downloading instead.`,
          variant: "destructive"
        });
        return;
      }
      
      if (inquiryId) {
        logActivity({ inquiryId, action: 'document_previewed', description: `Previewed document ${contractNumber}`, metadata: { contractNumber, storagePath } });
      }
    } catch (error: any) {
      console.error('❌ Preview error:', error);
      toast({ title: "Preview failed", description: error.message, variant: "destructive" });
    }
  };

  const attachContractToEmail = async (contract: any, inquiry: InquirySubmission) => {
    try {
      const { data, error } = await supabase.storage.from('contracts').download(contract.storage_path);
      if (error) throw error;
      const htmlContent = await data.text();
      setSelectedInquiry(inquiry);
      setContractPdf({ filename: `Contract-${contract.contract_number}.html`, content: htmlContent });
      setEmailComposerOpen(true);
    } catch (err: any) {
      toast({ title: "Failed to load contract", description: err.message, variant: "destructive" });
    }
  };

  const sendContractForSignature = async (contract: any, inquiry: InquirySubmission) => {
    try {
      toast({ title: "Sending contract...", description: "Preparing signing link" });
      const response = await supabase.functions.invoke('send-contract', { body: { contractId: contract.id } });
      if (response.error) throw new Error(response.error.message);
      toast({ title: "Contract Sent! ✓", description: `Signing link sent to ${contract.client_email}` });
      // Note: Activity logging is handled by the send-contract edge function to avoid duplicates
      
      // Status update is handled by the send-contract edge function
      
      fetchContractsForInquiry(inquiry.id);
      fetchInquiries();
    } catch (err: any) {
      toast({ title: "Failed to send contract", description: err.message, variant: "destructive" });
    }
  };

  const cancelContract = async (contract: any, inquiry: InquirySubmission) => {
    const reason = prompt("Optional: Enter a reason for cancellation (leave blank to skip)");
    if (reason === null) return; // User clicked Cancel on prompt
    
    try {
      toast({ title: "Cancelling contract...", description: "Notifying client" });
      const response = await supabase.functions.invoke('cancel-contract', { 
        body: { 
          contractId: contract.id, 
          reason: reason || undefined,
          notifyClient: true 
        } 
      });
      if (response.error) throw new Error(response.error.message);
      toast({ title: "Contract Cancelled ✓", description: `Cancellation notification sent to ${contract.client_email}` });
      
      // Status will be updated by the edge function
      fetchContractsForInquiry(inquiry.id);
      fetchInquiries();
    } catch (err: any) {
      toast({ title: "Failed to cancel contract", description: err.message, variant: "destructive" });
    }
  };

  // isCheckingExpired is declared above with other state variables
  
  const checkExpiredInquiries = async () => {
    setIsCheckingExpired(true);
    try {
      const response = await supabase.functions.invoke('check-expired-inquiries');
      if (response.error) throw new Error(response.error.message);
      const { closedWon, closedLost, processed } = response.data;
      toast({ 
        title: "Expired Inquiries Checked", 
        description: `Processed ${processed}: ${closedWon} closed-won, ${closedLost} closed-lost`
      });
    } catch (err: any) {
      // The function may have completed server-side even if the request timed out
      toast({ 
        title: "Check completed", 
        description: "Expired inquiries processed. Refreshing list...",
      });
    } finally {
      setIsCheckingExpired(false);
      fetchInquiries();
    }
  };

  const closedStatuses = ['closed_won', 'closed_lost'];
  const activeInquiries = inquiries.filter(i => !closedStatuses.includes(i.status));
  const historyInquiries = inquiries.filter(i => closedStatuses.includes(i.status));
  const displayedInquiries = activeTab === 'active' ? activeInquiries : historyInquiries;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-lg">Inquiries</CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkExpiredInquiries} 
              disabled={isCheckingExpired}
              title="Check for past-event inquiries and close them automatically"
            >
              <Clock className={`h-4 w-4 mr-2 ${isCheckingExpired ? 'animate-spin' : ''}`} />
              Close Expired
            </Button>
            <Button variant="outline" size="sm" onClick={fetchInquiries} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <div className="px-6 pb-2">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'active' | 'history')}>
            <TabsList>
              <TabsTrigger value="active" className="gap-1.5">
                Active
                {activeInquiries.length > 0 && (
                  <span className="text-[10px] bg-muted-foreground/20 rounded-full px-1.5">{activeInquiries.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-1.5">
                <Archive className="h-3.5 w-3.5" />
                History
                {historyInquiries.length > 0 && (
                  <span className="text-[10px] bg-muted-foreground/20 rounded-full px-1.5">{historyInquiries.length}</span>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : displayedInquiries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {activeTab === 'active' ? 'No active inquiries' : 'No closed inquiries yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="w-24">ID</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Scheduled Call</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedInquiries.map((inquiry) => (
                    <React.Fragment key={inquiry.id}>
                      <TableRow 
                        className={cn(
                          "cursor-pointer hover:bg-muted/30 transition-colors",
                          getRowColorByStatus(inquiry.status),
                          inquiry.has_incident && "animate-pulse border-l-4 !border-l-red-500"
                        )} 
                        onClick={() => toggleExpand(inquiry.id)}
                      >
                        <TableCell className="py-3">
                          {expandedRow === inquiry.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(inquiry.id);
                                setCopiedId(inquiry.id);
                                toast({ title: "Copied!", description: "Inquiry ID copied to clipboard" });
                                setTimeout(() => setCopiedId(null), 2000);
                              } catch {
                                toast({ title: "Copy failed", variant: "destructive" });
                              }
                            }}
                            className="group flex items-center gap-1 text-xs font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground hover:bg-muted-foreground/20 transition-colors cursor-pointer"
                            title="Click to copy full ID"
                          >
                            {inquiry.id.slice(0, 8)}
                            {copiedId === inquiry.id ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {inquiry.has_incident && <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                            <div>
                              <p className="font-medium text-sm">{inquiry.name}</p>
                              <p className="text-xs text-muted-foreground">{inquiry.email}</p>
                            </div>
                            {unreadCounts[inquiry.id] > 0 && (
                              <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] px-1 text-[11px] font-bold bg-red-500 text-white rounded-full leading-none">
                                {unreadCounts[inquiry.id]}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{inquiry.occasion || 'Event'}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(inquiry.event_date), "MMM d, yyyy")}
                              {inquiry.event_dates_json?.length > 1 && ` +${inquiry.event_dates_json.length - 1}`}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {inquiry.scheduled_call_at ? (
                            <div className="flex items-center gap-1.5">
                              <CalendarIcon className="h-3.5 w-3.5 text-primary" />
                              <div>
                                {(() => {
                                  // Convert UTC time to client's timezone for display
                                  const clientTz = inquiry.user_timezone || 'America/Chicago';
                                  const utcDate = new Date(inquiry.scheduled_call_at);
                                  const zonedDate = toZonedTime(utcDate, clientTz);
                                  const tzAbbr = (() => {
                                    try {
                                      const formatter = new Intl.DateTimeFormat('en-US', {
                                        timeZone: clientTz,
                                        timeZoneName: 'short'
                                      });
                                      const parts = formatter.formatToParts(utcDate);
                                      return parts.find(p => p.type === 'timeZoneName')?.value || '';
                                    } catch { return ''; }
                                  })();
                                  return (
                                    <>
                                      <p className="text-xs font-medium">
                                        {format(zonedDate, "MMM d, yyyy")}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {format(zonedDate, "h:mm a")}
                                        {tzAbbr && (
                                          <span className="ml-1 opacity-70">({tzAbbr})</span>
                                        )}
                                      </p>
                                    </>
                                  );
                                })()}
                              </div>
                              {inquiry.calendar_rsvp_status && (
                                <RsvpStatusBadge 
                                  status={inquiry.calendar_rsvp_status} 
                                  rsvpAt={inquiry.calendar_rsvp_at}
                                />
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-muted-foreground">—</span>
                              {inquiry.calendar_rsvp_status && (
                                <RsvpStatusBadge 
                                  status={inquiry.calendar_rsvp_status} 
                                  rsvpAt={inquiry.calendar_rsvp_at}
                                />
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            const contracts = inquiryContracts[inquiry.id] || [];
                            const latestContract = contracts[0];
                            const contractAmount = latestContract?.total_amount;
                            const originalCost = inquiry.total_cost;
                            if (contractAmount && originalCost && contractAmount < originalCost) {
                              const discountPct = Math.round((1 - contractAmount / originalCost) * 100);
                              return (
                                <div>
                                  <span className="text-xs text-muted-foreground line-through">{formatCurrency(originalCost)}</span>
                                  <span className="font-semibold text-primary ml-1">{formatCurrency(contractAmount)}</span>
                                  <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0">{discountPct}% off</Badge>
                                </div>
                              );
                            }
                            return <span className="font-semibold text-primary">{formatCurrency(originalCost)}</span>;
                          })()}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select value={inquiry.status} onValueChange={(v) => handleStatusChange(inquiry.id, v, inquiry.status)}>
                            <SelectTrigger className="w-[120px] h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-popover border shadow-lg z-50">
                              {STATUS_OPTIONS.map((status) => (
                                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Details */}
                      {expandedRow === inquiry.id && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-muted/20 p-0">
                            <div className="p-4 space-y-4">
                              {/* Main Content - Full Width */}
                              <InquiryDetailTabs 
                                inquiry={inquiry}
                                formatTime={formatTime}
                                formatCurrency={formatCurrency}
                                safeFormatEventDate={safeFormatEventDate}
                                onRefresh={() => {
                                  fetchInquiries();
                                  fetchContractsForInquiry(inquiry.id);
                                  fetchAssignmentsForInquiry(inquiry.id);
                                }}
                                onMusicianAssignmentChange={() => {
                                  fetchAssignmentsForInquiry(inquiry.id);
                                }}
                                musicianAssignments={inquiryAssignments[inquiry.id] || []}
                                onOpenContract={() => {
                                  setSelectedInquiry(inquiry);
                                  setEditingContract(null);
                                  setContractBuilderOpen(true);
                                }}
                                contracts={inquiryContracts[inquiry.id] || []}
                                onEditContract={(contract) => { 
                                  setSelectedInquiry(inquiry); 
                                  setEditingContract(contract); 
                                  setContractBuilderOpen(true); 
                                }}
                                onEditItinerary={(itinerary) => {
                                  setSelectedInquiry(inquiry);
                                  setEditingItinerary(itinerary);
                                  setItineraryEditorOpen(true);
                                }}
                                onPreviewContract={(storagePath, contractNumber) => previewContractFile(storagePath, contractNumber, inquiry.id)}
                                onDownloadContract={downloadContract}
                                onAttachToEmail={(contract) => attachContractToEmail(contract, inquiry)}
                                onSendForSignature={(contract) => sendContractForSignature(contract, inquiry)}
                                onDeleteContract={async (contract) => {
                                  if (!confirm(`Delete ${contract.contract_number}? This cannot be undone.`)) return;
                                  try {
                                    if (contract.storage_path) {
                                      await supabase.storage.from('contracts').remove([contract.storage_path]);
                                    }
                                    const { error } = await supabase.from('inquiry_contracts').delete().eq('id', contract.id);
                                    if (error) throw error;
                                    toast({ title: "Document deleted" });
                                    logActivity({
                                      inquiryId: inquiry.id,
                                      action: 'document_deleted',
                                      description: `Deleted document ${contract.contract_number}`,
                                      metadata: { contractNumber: contract.contract_number }
                                    });
                                    fetchContractsForInquiry(inquiry.id);
                                  } catch (err: any) {
                                    toast({ title: "Delete failed", description: err.message, variant: "destructive" });
                                  }
                                }}
                                onScheduleCall={() => openScheduleDialog(inquiry)}
                                onCancelCall={() => cancelScheduledCall(inquiry.id)}
                              />

                              {/* Secondary Panels - 1:2 ratio grid, natural heights */}
                              <div className="grid gap-3 md:grid-cols-3 items-start">
                                {/* Notes + Vendor Contacts + Documents (stacked in 1 column) */}
                                <div className="flex flex-col gap-3">
                                  <InquiryNotesPanel inquiryId={inquiry.id} initialNotes={inquiry.notes} />
                                  <VendorContactsPanel inquiryId={inquiry.id} initialContacts={inquiry.vendor_contacts_json as any} />
                                  <DocumentsPanel
                                    contracts={inquiryContracts[inquiry.id] || []}
                                    onPreview={(storagePath, contractNumber) => previewContractFile(storagePath, contractNumber, inquiry.id)}
                                    onDownload={downloadContract}
                                    onEdit={(contract) => { 
                                      setSelectedInquiry(inquiry); 
                                      setEditingContract(contract); 
                                      setContractBuilderOpen(true); 
                                    }}
                                    onDelete={async (contract) => {
                                      if (!confirm(`Delete ${contract.contract_number}? This cannot be undone.`)) return;
                                      try {
                                        if (contract.storage_path) {
                                          await supabase.storage.from('contracts').remove([contract.storage_path]);
                                        }
                                        const { error } = await supabase.from('inquiry_contracts').delete().eq('id', contract.id);
                                        if (error) throw error;
                                        toast({ title: "Document deleted" });
                                        logActivity({
                                          inquiryId: inquiry.id,
                                          action: 'document_deleted',
                                          description: `Deleted document ${contract.contract_number}`,
                                          metadata: { contractNumber: contract.contract_number }
                                        });
                                        fetchContractsForInquiry(inquiry.id);
                                      } catch (err: any) {
                                        toast({ title: "Delete failed", description: err.message, variant: "destructive" });
                                      }
                                    }}
                                    onAttachToEmail={(contract) => attachContractToEmail(contract, inquiry)}
                                    onSendForSignature={(contract) => sendContractForSignature(contract, inquiry)}
                                    onCancelContract={(contract) => cancelContract(contract, inquiry)}
                                  />
                                </div>

                                {/* Email Panel - 2/3 width, larger fixed height with internal scroll */}
                                <div className="border rounded-lg p-3 bg-card md:col-span-2 flex flex-col h-[800px] overflow-hidden">
                                  <h4 className="font-medium text-sm flex items-center gap-2 mb-2">
                                    <Mail className="h-4 w-4" /> Email
                                  </h4>
                                  <InlineEmailPanel
                                    inquiryId={inquiry.id}
                                    inquiryName={inquiry.name}
                                    inquiryEmail={inquiry.email}
                                    occasion={inquiry.occasion}
                                    totalCost={inquiry.total_cost}
                                    eventDatesJson={inquiry.event_dates_json}
                                    onPreviewDocument={(storagePath, name) => previewContractFile(storagePath, name, inquiry.id)}
                                  />
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Call Dialog with proper date/time pickers */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              Schedule Call with {selectedInquiry?.name}
            </DialogTitle>
            <DialogDescription>Set a date and time for your follow-up call.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Date Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                Select Date
              </Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !callDate && "text-muted-foreground"
                    )}
                  >
                    {callDate ? format(callDate, "EEEE, MMMM d, yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-popover border shadow-lg z-50" align="start">
                  <Calendar
                    mode="single"
                    selected={callDate}
                    onSelect={(date) => {
                      setCallDate(date);
                      setCalendarOpen(false);
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Selection - Grid of time slots */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Select Time
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {ADMIN_TIME_SLOTS.map((time) => (
                  <Button
                    key={time}
                    type="button"
                    variant={callTime === time ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCallTime(time)}
                    className={cn(
                      "text-sm",
                      callTime === time && "bg-primary text-primary-foreground"
                    )}
                  >
                    {formatTimeSlot(time)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea 
                value={callNotes} 
                onChange={(e) => setCallNotes(e.target.value)} 
                placeholder="Call agenda, topics to discuss..." 
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleScheduleCall} disabled={!callDate || !callTime}>
              Schedule Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Preview Dialog */}
      <Dialog open={!!previewContract} onOpenChange={() => setPreviewContract(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Contract Preview: {previewContract?.contractNumber}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-lg bg-white">
            {previewContract?.pdfUrl ? (
              <InlinePdfViewer url={previewContract.pdfUrl} height="70vh" />
            ) : previewContract?.imageUrl ? (
              <div className="flex items-center justify-center p-4 h-[70vh] bg-muted/20">
                <img src={previewContract.imageUrl} alt={previewContract.contractNumber} className="max-w-full max-h-full object-contain" />
              </div>
            ) : previewContract?.audioUrl ? (
              <div className="flex flex-col items-center justify-center gap-4 p-8 h-[70vh] bg-muted/20">
                <div className="text-6xl">🎵</div>
                <p className="text-lg font-medium text-foreground">{previewContract.contractNumber}</p>
                <audio controls className="w-full max-w-md" src={previewContract.audioUrl}>
                  Your browser does not support the audio element.
                </audio>
              </div>
            ) : previewContract?.videoUrl ? (
              <div className="flex items-center justify-center p-4 h-[70vh] bg-black">
                <video controls className="max-w-full max-h-full" src={previewContract.videoUrl}>
                  Your browser does not support the video element.
                </video>
              </div>
            ) : previewContract?.officeUrl ? (
              <iframe src={previewContract.officeUrl} className="w-full h-[70vh] border-0" title="Office Document Preview" />
            ) : previewContract?.textContent ? (
              <div className="p-4 h-[70vh] overflow-auto bg-muted/10">
                <pre className="text-sm font-mono whitespace-pre-wrap break-words text-foreground">{previewContract.textContent}</pre>
              </div>
            ) : previewContract?.html ? (
              <iframe srcDoc={previewContract.html} className="w-full h-[70vh] border-0" title="Document Preview" />
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewContract(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRM Dialogs */}
      {selectedInquiry && (
        <>
          <EmailComposer
            open={emailComposerOpen}
            onOpenChange={setEmailComposerOpen}
            inquiry={selectedInquiry}
            contractPdf={contractPdf}
            onSuccess={fetchInquiries}
          />
          <ContractBuilder
            open={contractBuilderOpen}
            onOpenChange={(open) => { setContractBuilderOpen(open); if (!open) setEditingContract(null); }}
            inquiry={selectedInquiry}
            existingContract={editingContract}
            onContractGenerated={(pdf) => { setContractPdf(pdf); setContractBuilderOpen(false); setEditingContract(null); setEmailComposerOpen(true); }}
            onContractSaved={() => { fetchContractsForInquiry(selectedInquiry.id); setEditingContract(null); }}
          />
        </>
      )}

      {/* Itinerary Editor */}
      {editingItinerary && (
        <ItineraryEditor
          open={itineraryEditorOpen}
          onOpenChange={(open) => { setItineraryEditorOpen(open); if (!open) setEditingItinerary(null); }}
          document={editingItinerary}
          onSaved={() => { 
            if (selectedInquiry) fetchContractsForInquiry(selectedInquiry.id); 
            setEditingItinerary(null); 
          }}
        />
      )}
    </>
  );
};
